import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Chip,
  Paper,
  FormControlLabel,
  Switch,
  Tooltip,
  Radio,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Stars as CoordinatorIcon,
  WarningAmber as WarningIcon,
  LabelOutlined as KeywordIcon,
  PersonAddAlt1 as PersonAddIcon,
  ExpandLess as ExpandLessIcon,
  FileDownload as DownloadIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { useTheme } from '@mui/material/styles';

const EditConsortiumDialog = ({ open, onClose, consortium, onSuccess }) => {
  const theme = useTheme();

  const [consortiumName, setConsortiumName] = useState('');
  const [consortiumTitle, setConsortiumTitle] = useState('');
  const [description, setDescription] = useState('');
  const [members, setMembers] = useState([]);
  const [isPublic, setIsPublic] = useState(true);
  const [keywords, setKeywords] = useState([]);
  const [showKeywords, setShowKeywords] = useState(true);
  const [keywordInput, setKeywordInput] = useState('');
  const [coordinatorId, setCoordinatorId] = useState(null); // membership id of current coordinator

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [coordinatorUpdating, setCoordinatorUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [confirmCoordOpen, setConfirmCoordOpen] = useState(false);
  const [pendingCoordinatorId, setPendingCoordinatorId] = useState(null);

  const importFileRef = useRef(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importError, setImportError] = useState(null);

  const [addExternalOpen, setAddExternalOpen] = useState(false);
  const [extPersonName, setExtPersonName] = useState('');
  const [extOrgName, setExtOrgName] = useState('');
  const [extEmail, setExtEmail] = useState('');
  const [extRole, setExtRole] = useState('researcher');
  const [addingExternal, setAddingExternal] = useState(false);
  const [addExternalError, setAddExternalError] = useState(null);

  useEffect(() => {
    if (!open || !consortium) return;
    setConsortiumName(consortium.name || '');
    setConsortiumTitle(consortium.title || '');
    setDescription(consortium.summary || '');
    setError(null);
    setSuccess(false);
    setCoordinatorUpdating(false);

    const memberships = consortium.memberships || [];
    setMembers(memberships);

    // Derive current coordinator from roles
    const coordMembership = memberships.find(m =>
      (m.roles || []).some(r => r === 'coordinator')
    );
    setCoordinatorId(coordMembership?.id || null);

    setIsPublic(consortium.is_public ?? true);
    setKeywords(Array.isArray(consortium.keywords) ? consortium.keywords : []);
    setShowKeywords(consortium.show_keywords ?? true);
    setKeywordInput('');
    axiosService.get(`/consortium/consortiums/${consortium.id}/settings/`)
      .then(res => {
        setIsPublic(res.data.is_public ?? true);
        if (Array.isArray(res.data.keywords)) setKeywords(res.data.keywords);
        setShowKeywords(res.data.show_keywords ?? true);
      })
      .catch(() => {});
  }, [open, consortium]);

  const handleRemoveMember = async (membershipId) => {
    if (!window.confirm('Are you sure you want to remove this member from the consortium?')) return;
    setLoading(true);
    try {
      await axiosService.delete(`/consortium/memberships/${membershipId}/`);
      setMembers(prev => prev.filter(m => m.id !== membershipId));
      if (coordinatorId === membershipId) setCoordinatorId(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const handleSetCoordinator = (membershipId) => {
    if (coordinatorId === membershipId) return;
    setPendingCoordinatorId(membershipId);
    setConfirmCoordOpen(true);
  };

  const handleConfirmCoordinator = async () => {
    setConfirmCoordOpen(false);
    setCoordinatorUpdating(true);
    try {
      await axiosService.patch(`/consortium/memberships/${pendingCoordinatorId}/set-coordinator/`);
      if (onSuccess) onSuccess();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to transfer coordinator role');
      setCoordinatorUpdating(false);
    }
  };

  const handleCancelCoordinator = () => {
    setConfirmCoordOpen(false);
    setPendingCoordinatorId(null);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosService.get(
        '/consortium/memberships/member-import-template/',
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'otable_members_template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download template');
    }
  };

  const handleImportMembers = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResults(null);
    setImportError(null);
    try {
      const formData = new FormData();
      formData.append('consortium', consortium.id);
      formData.append('file', importFile);
      const res = await axiosService.post(
        '/consortium/memberships/import-members/',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setImportResults(res.data);
      if (res.data.added > 0 && onSuccess) onSuccess();
    } catch (err) {
      setImportError(err.response?.data?.detail || 'Import failed. Please check the file and try again.');
    } finally {
      setImporting(false);
    }
  };

  const handleAddExternalMember = async () => {
    if (!extPersonName.trim()) {
      setAddExternalError('Name is required');
      return;
    }
    setAddingExternal(true);
    setAddExternalError(null);
    try {
      const res = await axiosService.post(
        '/consortium/memberships/add-external-member/',
        {
          consortium: consortium.id,
          person_name: extPersonName.trim(),
          organisation_name: extOrgName.trim(),
          email: extEmail.trim() || undefined,
          role: extRole,
        }
      );
      // Add optimistic member row so the list updates immediately
      const newMembership = {
        id: res.data.id,
        organization: { id: null, name: extOrgName.trim() || 'External', short_name: '' },
        member: { id: null, name: extPersonName.trim(), is_external: true, avatar: null },
        roles: [extRole],
      };
      setMembers(prev => [...prev, newMembership]);
      setExtPersonName('');
      setExtOrgName('');
      setExtEmail('');
      setExtRole('researcher');
      setAddExternalOpen(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      setAddExternalError(
        err.response?.data?.detail || 'Failed to add external member'
      );
    } finally {
      setAddingExternal(false);
    }
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const kw = keywordInput.trim().replace(/,$/, '');
      if (kw && !keywords.includes(kw)) setKeywords(prev => [...prev, kw]);
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (kw) => setKeywords(prev => prev.filter(k => k !== kw));

  const handleSubmit = async () => {
    if (!consortiumName.trim()) {
      setError('Consortium name is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      try {
        await axiosService.patch(
          `/consortium/consortiums/${consortium.id}/update-details/`,
          {
            name: consortiumName.trim(),
            title: consortiumTitle.trim(),
            description: description.trim(),
          }
        );
      } catch (endpointErr) {
        if (endpointErr.response?.status === 404) {
          await axiosService.patch(`/consortium/consortiums/${consortium.id}/`, {
            name: consortiumName.trim(),
          });
        } else {
          throw endpointErr;
        }
      }
      await axiosService.patch(
        `/consortium/consortiums/${consortium.id}/settings/`,
        { is_public: isPublic, keywords, show_keywords: showKeywords }
      );
      setSuccess(true);
      if (onSuccess) onSuccess();
      setTimeout(() => handleClose(), 1500);
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'Failed to update consortium. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setConsortiumName('');
    setConsortiumTitle('');
    setDescription('');
    setMembers([]);
    setKeywords([]);
    setShowKeywords(true);
    setKeywordInput('');
    setError(null);
    setSuccess(false);
    setAddExternalOpen(false);
    setExtPersonName('');
    setExtOrgName('');
    setExtEmail('');
    setExtRole('researcher');
    setAddExternalError(null);
    setImportFile(null);
    setImportResults(null);
    setImportError(null);
    if (importFileRef.current) importFileRef.current.value = '';
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 3000 }}
      slotProps={{ paper: { sx: { borderRadius: 1 } } }}
    >
      <DialogTitle>
        <Typography variant="h5" fontWeight={600}>
          Edit Consortium
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Update consortium name, description, visibility, and manage members
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Consortium updated successfully!
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Name"
              value={consortiumName}
              onChange={(e) => setConsortiumName(e.target.value)}
              disabled={submitting}
              required
              sx={{ flex: 1 }}
            />
            <TextField
              label="Consortium Title"
              value={consortiumTitle}
              onChange={(e) => setConsortiumTitle(e.target.value)}
              disabled={submitting}
              placeholder={`Consortium for ${consortiumName || '…'}`}
              sx={{ flex: 4 }}
            />
          </Box>

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
            helperText="Brief summary of the consortium's objectives"
            sx={{ mb: 2 }}
          />

          {/* Visibility */}
          <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              {isPublic
                ? <VisibilityIcon color="primary" sx={{ mr: 1 }} />
                : <VisibilityOffIcon color="action" sx={{ mr: 1 }} />}
              <Typography variant="subtitle1" fontWeight={600}>Visibility</Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  color="primary"
                  disabled={submitting}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Public Consortium</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isPublic
                      ? 'Visible to everyone on the consortium list'
                      : 'Only visible to consortium members'}
                  </Typography>
                </Box>
              }
            />

            <Box sx={{ mt: 2, borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.75 }}>
                <KeywordIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="subtitle2">Research Keywords</Typography>
              </Box>

              <TextField
                placeholder="Type a keyword and press Enter"
                size="small"
                fullWidth
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={handleKeywordKeyDown}
                disabled={submitting}
                sx={{ mb: 1 }}
              />

              {keywords.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                  {keywords.map((kw) => (
                    <Chip
                      key={kw}
                      label={kw}
                      size="small"
                      onDelete={() => handleRemoveKeyword(kw)}
                      disabled={submitting}
                    />
                  ))}
                </Box>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={showKeywords}
                    onChange={(e) => setShowKeywords(e.target.checked)}
                    color="primary"
                    size="small"
                    disabled={submitting}
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    Show keywords publicly on the consortium card
                  </Typography>
                }
              />
            </Box>
          </Paper>

          {/* Members */}
          <Typography variant="h6" gutterBottom sx={{ mt: 1, mb: 0.5 }}>
            Consortium Members
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Hover over a member row to assign them as coordinator.
          </Typography>

          {members.length > 0 && (
            <List disablePadding>
              {members.map((membership) => {
                const isCoord = coordinatorId === membership.id;
                const otherRoles = (membership.roles || []).filter(r => r !== 'coordinator');
                return (
                  <ListItem
                    key={membership.id}
                    secondaryAction={
                      <Tooltip title="Remove member">
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveMember(membership.id)}
                          disabled={loading || submitting || coordinatorUpdating}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: isCoord
                        ? theme.palette.primary.main + '12'
                        : theme.palette.background.default,
                      border: '1px solid',
                      borderColor: isCoord ? 'primary.main' : 'transparent',
                      transition: 'background-color 0.15s, border-color 0.15s',
                      '&:hover': {
                        bgcolor: isCoord
                          ? theme.palette.primary.main + '1a'
                          : theme.palette.action.hover,
                      },
                      '&:hover .coord-radio': { opacity: 1 },
                    }}
                  >
                    {/* Coordinator radio — hidden until hover (or always visible when selected) */}
                    <Tooltip title={membership.member?.is_external ? 'External members cannot be coordinator until they activate their account' : isCoord ? 'Coordinator' : 'Set as coordinator'}>
                      <Radio
                        className="coord-radio"
                        checked={isCoord}
                        onChange={() => handleSetCoordinator(membership.id)}
                        disabled={coordinatorUpdating || submitting || membership.member?.is_external}
                        size="small"
                        color="primary"
                        sx={{
                          opacity: isCoord ? 1 : 0,
                          transition: 'opacity 0.15s',
                          p: 0.5,
                          mr: 0.5,
                        }}
                      />
                    </Tooltip>

                    <ListItemAvatar>
                      <Avatar
                        src={membership.member?.avatar}
                        alt={membership.member?.name}
                        sx={{
                          bgcolor: isCoord ? 'primary.main' : theme.palette.grey[400],
                          color: 'white',
                          width: 36,
                          height: 36,
                        }}
                      >
                        {membership.member?.name
                          ?.split(' ')
                          .map((n) => n[0])
                          .join('')
                          .toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="body2" fontWeight={500}>
                            {membership.member?.name || 'Unknown'}
                          </Typography>
                          {isCoord && (
                            <Chip
                              icon={<CoordinatorIcon sx={{ fontSize: 14 }} />}
                              label="Coordinator"
                              size="small"
                              color="primary"
                              sx={{ fontWeight: 600, height: 20, fontSize: 11 }}
                            />
                          )}
                          {membership.member?.is_external && (
                            <Chip
                              label="External"
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: 11, color: 'text.secondary', borderColor: 'divider' }}
                            />
                          )}
                          {otherRoles.map((role, idx) => (
                            <Chip
                              key={idx}
                              label={role.replace('_', ' ')}
                              size="small"
                              sx={{ textTransform: 'capitalize', height: 20, fontSize: 11 }}
                            />
                          ))}
                        </Box>
                      }
                      secondary={membership.organization?.name || 'No organization'}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}

          {/* Add external member + bulk import — single toolbar row */}
          <Divider sx={{ mt: 2, mb: 1 }} />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              size="small"
              startIcon={addExternalOpen ? <ExpandLessIcon /> : <PersonAddIcon />}
              onClick={() => { setAddExternalOpen(o => !o); setAddExternalError(null); }}
              disabled={submitting}
            >
              {addExternalOpen ? 'Cancel' : 'Add external member'}
            </Button>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              disabled={submitting}
            >
              Download template
            </Button>

            <input
              ref={importFileRef}
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              onChange={e => {
                setImportFile(e.target.files[0] || null);
                setImportResults(null);
                setImportError(null);
              }}
            />
            <Button
              size="small"
              startIcon={<UploadIcon />}
              onClick={() => importFileRef.current?.click()}
              disabled={submitting || importing}
            >
              {importFile ? importFile.name : 'Import from Excel'}
            </Button>

            {importFile && (
              <Button
                size="small"
                variant="contained"
                onClick={handleImportMembers}
                disabled={importing}
                startIcon={importing ? <CircularProgress size={14} color="inherit" /> : null}
              >
                {importing ? 'Importing…' : 'Import'}
              </Button>
            )}
          </Box>

          {/* Add external member inline form */}
          <Collapse in={addExternalOpen}>
            <Paper elevation={0} sx={{ p: 2, mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {addExternalError && (
                <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setAddExternalError(null)}>
                  {addExternalError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  label="Name *"
                  value={extPersonName}
                  onChange={e => setExtPersonName(e.target.value)}
                  size="small"
                  disabled={addingExternal}
                  sx={{ flex: '1 1 150px' }}
                />
                <TextField
                  label="Organisation"
                  value={extOrgName}
                  onChange={e => setExtOrgName(e.target.value)}
                  size="small"
                  disabled={addingExternal}
                  sx={{ flex: '1 1 150px' }}
                />
                <TextField
                  label="Email (optional)"
                  type="email"
                  value={extEmail}
                  onChange={e => setExtEmail(e.target.value)}
                  size="small"
                  disabled={addingExternal}
                  sx={{ flex: '1 1 150px' }}
                />
                <FormControl size="small" sx={{ flex: '0 0 160px' }}>
                  <InputLabel>Role</InputLabel>
                  <Select value={extRole} label="Role" onChange={e => setExtRole(e.target.value)} disabled={addingExternal}>
                    <MenuItem value="external_coordinator">External Coordinator</MenuItem>
                    <MenuItem value="researcher">Researcher</MenuItem>
                    <MenuItem value="contributor">Contributor</MenuItem>
                    <MenuItem value="wp_leader">WP Leader</MenuItem>
                    <MenuItem value="task_leader">Task Leader</MenuItem>
                    <MenuItem value="communicator">Communicator</MenuItem>
                    <MenuItem value="reviewer">Reviewer</MenuItem>
                    <MenuItem value="coordinator">Coordinator</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddExternalMember}
                  disabled={addingExternal || !extPersonName.trim()}
                  startIcon={addingExternal ? <CircularProgress size={14} color="inherit" /> : <PersonAddIcon />}
                  sx={{ alignSelf: 'center', whiteSpace: 'nowrap' }}
                >
                  {addingExternal ? 'Adding…' : 'Add'}
                </Button>
              </Box>
            </Paper>
          </Collapse>

          {/* Import feedback */}
          {importError && (
            <Alert severity="error" sx={{ mt: 1 }} onClose={() => setImportError(null)}>
              {importError}
            </Alert>
          )}
          {importResults && (
            <Alert severity={importResults.errors?.length > 0 ? 'warning' : 'success'} sx={{ mt: 1 }}>
              {importResults.added} member{importResults.added !== 1 ? 's' : ''} added
              {importResults.errors?.length > 0 && (
                <>
                  {' · '}{importResults.errors.length} row{importResults.errors.length !== 1 ? 's' : ''} skipped
                  <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
                    {importResults.errors.map((e, i) => (
                      <li key={i}>
                        <Typography variant="caption">Row {e.row}: <strong>{e.name}</strong> — {e.detail}</Typography>
                      </li>
                    ))}
                  </Box>
                </>
              )}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !consortiumName.trim()}
          startIcon={submitting && <CircularProgress size={16} />}
        >
          {submitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>

      {/* Coordinator transfer confirmation */}
      <Dialog
        open={confirmCoordOpen}
        onClose={handleCancelCoordinator}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 3100 }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Transfer Coordinator Role
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            You are about to hand over the coordinator role to{' '}
            <Box component="span" fontWeight={600}>
              {members.find(m => m.id === pendingCoordinatorId)?.member?.name || 'this member'}
            </Box>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You will lose coordinator access immediately. The Edit Consortium dialog will close and you will no longer be able to manage this consortium's settings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCoordinator} disabled={coordinatorUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCoordinator}
            variant="contained"
            color="warning"
            disabled={coordinatorUpdating}
            startIcon={coordinatorUpdating && <CircularProgress size={16} color="inherit" />}
          >
            {coordinatorUpdating ? 'Transferring…' : 'Transfer Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default EditConsortiumDialog;
